import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Link } from "react-router-dom";
import { brainTokens } from "../design/brainTheme";
import { REF_ROUTES, refLabel, refExternalHref } from "../agent/RefText";

const c = brainTokens.color;

const chipSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontFamily: brainTokens.font.mono,
  fontSize: 11,
  color: c.citeText,
  background: c.citeBg,
  border: `1px solid ${c.citeBorder}`,
  borderRadius: "5px",
  padding: "0 5px",
  cursor: "pointer",
  textDecoration: "none",
  lineHeight: 1.7,
  "&:hover": { background: "#e3f0f0" },
};

/**
 * Mono teal citation chip. Reuses RefText's REF_ROUTES/refLabel (the single
 * ref→route source) rather than duplicating the map. Renders a Link when the
 * ref resolves in-app, an override `href` (e.g. a real Jira URL) when given,
 * else a non-navigating span — never a broken link.
 */
export function CitationChip({
  type,
  id,
  href,
  label,
}: {
  type: string;
  id: string;
  /** External URL override (e.g. Jira). Opens in a new tab. */
  href?: string;
  label?: string;
}) {
  const text = label ?? refLabel(type, id);
  const inAppRoute = REF_ROUTES[type]?.(id) ?? null;
  const external = href ?? refExternalHref(type, id) ?? undefined;

  if (external) {
    return (
      <Box component="a" href={external} target="_blank" rel="noopener noreferrer" title={`${type}:${id}`} sx={chipSx}>
        {text}
      </Box>
    );
  }
  if (inAppRoute) {
    return (
      <Box component={Link} to={inAppRoute} title={`${type}:${id}`} sx={chipSx}>
        {text}
      </Box>
    );
  }
  return (
    <Box component="span" title={`${type}:${id}`} sx={{ ...chipSx, cursor: "default" }}>
      {text}
    </Box>
  );
}
